import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';

const RETRY_AFTER_REFRESH_HEADER = 'X-Retry-After-Refresh';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    constructor(private authService: AuthenticationService) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        // Skip token attachment for external services
        if (req.url.includes('freegeoip.app') || req.url.includes('forfettapp.blob') || req.url.includes('ficblob.blob')) {
            return next.handle(req);
        }

        return next.handle(this.addTokenToRequest(req)).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    // Prevent infinite retry loop: if already retried after refresh, don't retry again
                    if (req.headers.has(RETRY_AFTER_REFRESH_HEADER)) {
                        return throwError(error);
                    }
                    return this.handle401Error(req, next);
                }
                return throwError(error);
            })
        );
    }

    private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return this.authService.handleTokenRefresh().pipe(
            switchMap((newAccessToken: string) => {
                const retryRequest = request.clone({
                    headers: request.headers.set(RETRY_AFTER_REFRESH_HEADER, 'true'),
                });
                return next.handle(this.addTokenToRequest(retryRequest, newAccessToken));
            }),
            catchError((error: HttpErrorResponse) => {
                // Refresh failed. On a definitive failure handleTokenRefresh() already logged
                // out; on a transient one the session is kept alive and the original request
                // simply fails, so the user can retry without losing the session.
                return throwError(error);
            })
        );
    }

    private addTokenToRequest(req: HttpRequest<unknown>, overrideToken?: string): HttpRequest<unknown> {
        const token = overrideToken ?? this.authService.getAuthTokenOrDefault()?.accessToken ?? null;

        // Strip custom 'api' header if present
        if (req.headers.has('api')) {
            req = req.clone({ headers: req.headers.delete('api') });
        }

        return token ? req.clone({ setHeaders: { Authorization: 'Bearer ' + token } }) : req;
    }
}
